export type SitarMeendMode = "beginner" | "standard" | "expert";

export type DaegeumCheongState = {
  cheongTension: number;
  cheongExposure: number;
  breathPressure: number;
};

export type OudCourse = {
  courseId: string;
  stringCount: 1 | 2;
  baseHz: number;
  detuneCentsPair?: [number, number];
};

export const sitarMeendLimits = {
  beginnerSoftLimitCents: 300,
  realisticUpperLimitCents: 700,
  defaultMaxCents: 500,
  expertMaxCents: 700
} as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function pitchRatioFromCents(cents: number): number {
  return 2 ** (cents / 1200);
}

export function centsToFrequency(tonicHz: number, cents: number): number {
  return tonicHz * pitchRatioFromCents(cents);
}

export function playbackRateForSample(targetHz: number, recordedHz: number): number {
  if (recordedHz <= 0) {
    throw new Error("recordedHz must be greater than zero.");
  }

  return targetHz / recordedHz;
}

export function applyPitchBend(
  detuneParam: AudioParam,
  context: BaseAudioContext,
  points: Array<{ time: number; cents: number }>
): void {
  if (points.length === 0) {
    return;
  }

  detuneParam.cancelScheduledValues(context.currentTime);
  detuneParam.setValueAtTime(points[0].cents, context.currentTime + points[0].time);

  for (const point of points.slice(1)) {
    detuneParam.linearRampToValueAtTime(point.cents, context.currentTime + point.time);
  }
}

export function gayageumNonghyeonCurve(baseCents: number, depthCents: number, t: number): number {
  const wobble = Math.sin(t * Math.PI * 2 * 5);
  const upwardOnly = Math.max(0, wobble);

  return baseCents + upwardOnly * Math.max(0, depthCents);
}

export function gayageumDecayWithNonghyeon(
  baseDecay: number,
  baseRelease: number,
  depthCents: number
): { decay: number; release: number } {
  const depth01 = clamp(depthCents / 180, 0, 1);

  return {
    decay: baseDecay * (1 - depth01 * 0.35),
    release: baseRelease * (1 - depth01 * 0.25)
  };
}

export function erhuPortamentoSmoothstep(startHz: number, endHz: number, progress: number): number {
  const t = clamp(progress, 0, 1);
  const eased = t * t * (3 - 2 * t);

  return startHz + (endHz - startHz) * eased;
}

export function erhuPortamentoLogistic(startHz: number, endHz: number, progress: number, steepness = 10): number {
  const t = clamp(progress, 0, 1);
  const logistic = 1 / (1 + Math.exp(-steepness * (t - 0.5)));
  const start = 1 / (1 + Math.exp(steepness * 0.5));
  const end = 1 / (1 + Math.exp(-steepness * 0.5));
  const normalized = (logistic - start) / (end - start);

  return startHz + (endHz - startHz) * normalized;
}

export function tablaBayanPressureBend(baseHz: number, pressure: number, maxRiseCents = 250): number {
  const normalizedPressure = clamp(pressure, 0, 1);
  const cents = normalizedPressure * maxRiseCents;

  return centsToFrequency(baseHz, cents);
}

export function tablaBayanReleaseFall(currentHz: number, baseHz: number, progress: number): number {
  const t = clamp(progress, 0, 1);
  const eased = t * t * (3 - 2 * t);

  return currentHz + (baseHz - currentHz) * eased;
}

export function createOmbakPairFrequencies(baseHz: number, detuneCents = 8): { lower: number; upper: number } {
  const detune = Math.abs(detuneCents);

  return {
    lower: centsToFrequency(baseHz, -detune),
    upper: centsToFrequency(baseHz, detune)
  };
}

export function clampSitarMeend(cents: number, mode: SitarMeendMode): number {
  const limitByMode: Record<SitarMeendMode, number> = {
    beginner: sitarMeendLimits.beginnerSoftLimitCents,
    standard: sitarMeendLimits.defaultMaxCents,
    expert: sitarMeendLimits.expertMaxCents
  };
  const limit = limitByMode[mode];

  return clamp(cents, 0, limit);
}

export function daegeumCheongBuzzAmount(state: DaegeumCheongState): number {
  const pressure = Math.pow(clamp(state.breathPressure, 0, 1), 1.4);
  const tension = clamp(state.cheongTension, 0, 1);
  const exposure = clamp(state.cheongExposure, 0, 1);
  const looseMembraneBuzz = 1 - tension;

  return clamp(looseMembraneBuzz * 0.45 + exposure * pressure * 0.75, 0, 1);
}

export function createOudCourseFrequencies(course: OudCourse): number[] {
  if (course.stringCount === 1) {
    return [course.baseHz];
  }

  const [a, b] = course.detuneCentsPair ?? [-4, 4];

  return [centsToFrequency(course.baseHz, a), centsToFrequency(course.baseHz, b)];
}
